import React from 'react';
import {
  CaseStudyDoubleCardRow,
  CaseStudyFindingBlock,
  CaseStudyFindingHeader,
} from '../../../components/cards/caseStudyCards/caseStudyCards';
import cards from '../../../components/cards/caseStudyCards/caseStudyCards.module.scss';
import ImageLightbox from '../../../components/imageLightbox/ImageLightbox';
import { useImageLightbox } from '../../../components/imageLightbox/useImageLightbox';
import d from './nbnbDesignSection.module.scss';
import page from './nbnb.module.scss';

import logo1 from '../../../assets/images/nbnb/logo-variation-1.png';
import logo2 from '../../../assets/images/nbnb/logo-variation-2.png';
import logo3 from '../../../assets/images/nbnb/logo-variation-3.png';
import logo4 from '../../../assets/images/nbnb/logo-variation-4.png';
import logo5 from '../../../assets/images/nbnb/logo-variation-5.png';
import moodboard1 from '../../../assets/images/nbnb/moodboard-1.png';
import moodboard2 from '../../../assets/images/nbnb/moodboard-2.png';
import moodboard3 from '../../../assets/images/nbnb/moodboard-3.png';
import moodboard4 from '../../../assets/images/nbnb/moodboard-4.png';
import colorMock1 from '../../../assets/images/nbnb/color-mock-1.png';
import colorMock2 from '../../../assets/images/nbnb/color-mock-2.png';
import colorMock3 from '../../../assets/images/nbnb/color-mock-3.png';
import colorMock4 from '../../../assets/images/nbnb/color-mock-4.png';
import quoteAvatarP9 from '../../../assets/images/nbnb/quote-avatar-p9.png';
import typographySelections from '../../../assets/images/nbnb/typography-selections.png';
import typographyPoppinsSpec from '../../../assets/images/nbnb/typography-poppins-spec.png';
import typographyUiMockups from '../../../assets/images/nbnb/typography-ui-mockups.png';
import uiLoginForm from '../../../assets/images/nbnb/ui-login-form.png';
import uiIconsGrid from '../../../assets/images/nbnb/ui-icons-grid.png';
import uiButtons from '../../../assets/images/nbnb/ui-buttons.png';
import photographyCollage from '../../../assets/images/nbnb/photography-collage.png';
import sectionDivider from '../../../assets/images/conditionsCaseStudy/designFinding/section_divider.svg';

const MOODBOARD_ALTS = [
  'Moodboard direction 1 for No Bunkers No Bogeys visual identity',
  'Moodboard direction 2 for No Bunkers No Bogeys visual identity',
  'Moodboard direction 3 for No Bunkers No Bogeys visual identity',
  'Moodboard direction 4 for No Bunkers No Bogeys visual identity',
];

const COLOR_MOCK_ALTS = [
  'Hi-fidelity color palette iteration mockup 1',
  'Hi-fidelity color palette iteration mockup 2',
  'Hi-fidelity color palette iteration mockup 3',
  'Hi-fidelity color palette iteration mockup 4',
];

const TYPOGRAPHY_SELECTIONS_ALT = 'Selected mockups of typography selections';

function ExpandableImageButton({ src, alt, imgClassName, buttonClassName = '', onOpen }) {
  return (
    <button
      type="button"
      className={`${page.expandableImageButton} ${buttonClassName}`.trim()}
      onClick={(e) => onOpen({ src, alt }, e.currentTarget)}
      aria-label={`View full size: ${alt}`}
    >
      <img className={imgClassName} src={src} alt="" loading="lazy" />
    </button>
  );
}

function SectionDivider() {
  return (
    <div className={d.divider} aria-hidden>
      <img className={d.dividerImg} src={sectionDivider} alt="" />
    </div>
  );
}

function ColorSwatches() {
  const swatches = [
    { name: 'Deep Grass', hex: '#1A2F1A', className: d.colorSwatchDeepGrass },
    { name: 'Sand', hex: '#FFF2DD', className: d.colorSwatchSand },
    { name: 'Neon', hex: '#EDF979', className: d.colorSwatchNeon },
    { name: 'Blush', hex: '#F8CED3', className: d.colorSwatchBlush },
  ];

  return (
    <div className={d.colorSwatchGrid}>
      <div className={d.colorSwatchRow}>
        {swatches.slice(0, 2).map((s) => (
          <div key={s.hex} className={`${d.colorSwatch} ${s.className}`}>
            <p className={d.colorSwatchName}>{s.name}</p>
            <p className={d.colorSwatchHex}>{s.hex}</p>
          </div>
        ))}
      </div>
      <div className={d.colorSwatchRow}>
        {swatches.slice(2).map((s) => (
          <div key={s.hex} className={`${d.colorSwatch} ${s.className}`}>
            <p className={d.colorSwatchName}>{s.name}</p>
            <p className={d.colorSwatchHex}>{s.hex}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NbnbDesignSection() {
  const { image, openLightbox, closeLightbox, closeButtonRef } = useImageLightbox();

  return (
    <div className={d.sectionGap}>
      <header className={d.intro}>
        <p className={d.introKicker}>VISUAL STRATEGY</p>
        <h2 className={d.introTitle}>Reimagining the Visual Language of Golf</h2>
      </header>

      <CaseStudyDoubleCardRow
        className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
        left={
          <div className={d.cardCopy}>
            <p>
              I intentionally pushed the visual system away from the traditional aesthetics dominating the
              industry.
            </p>
            <p>
              The goal was to create a brand that felt curious, modern, and community-driven while still
              maintaining enough structure and restraint to feel scalable and credible.
            </p>
            <p>
              The logo had already been defined, this was the one pre-existing brand element I was working to
              build upon. To kickoff the design process, I developed four moodboards to present and discuss
              with the founders and begin building a shared visual vocabulary.
            </p>
            <p>The final visual identity introduced:</p>
            <ul>
              <li>vibrant accent colors alongside softer cream and green tones</li>
              <li>accessible typography with rounded edges</li>
              <li>editorial-style photography featuring diverse representation</li>
              <li>curved image treatments and softer UI elements to create a more welcoming tone</li>
            </ul>
          </div>
        }
        right={
          <div className={d.logoBlock}>
            <div className={d.logoGridStack}>
              <div className={d.logoGridTop}>
                <img className={`${d.logoGridImg} ${d.logoGridImgSm}`} src={logo1} alt="" loading="lazy" />
                <img className={`${d.logoGridImg} ${d.logoGridImgSm}`} src={logo2} alt="" loading="lazy" />
                <img className={`${d.logoGridImg} ${d.logoGridImgSm}`} src={logo3} alt="" loading="lazy" />
              </div>
              <div className={d.logoGridBottom}>
                <img className={d.logoGridImgLg} src={logo4} alt="" loading="lazy" />
                <img className={d.logoGridImgLg} src={logo5} alt="" loading="lazy" />
              </div>
            </div>
            <p className={d.mediaCaption}>Logo Variations</p>
          </div>
        }
      />

      <div className={`${d.blockStack} ${d.moodboardStack} ${d.mediaCaptionStack}`}>
        <div className={d.moodboardRow}>
          {[moodboard1, moodboard2, moodboard3, moodboard4].map((src, index) => (
            <ExpandableImageButton
              key={src}
              src={src}
              alt={MOODBOARD_ALTS[index]}
              imgClassName={d.moodboardImg}
              buttonClassName={d.expandableImageButtonAuto}
              onOpen={openLightbox}
            />
          ))}
        </div>
        <p className={d.mediaCaption}>
          Four moodboard directions for No Bunkers No Bogeys visual direction
        </p>
      </div>

      <SectionDivider />

      <CaseStudyFindingBlock>
        <div className={d.findingStack}>
          <CaseStudyFindingHeader showMark={false} title="Color: Building A Bold Yet Referential Palette" />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.cardCopy}>
                <p>
                  In the industry and across competitors, popular colors are green, khaki, bright white, black
                  and navy blue. These colors have a traditional, elite, formal and masculine association,
                  while drawing on common associations with golf.
                </p>
                <p>
                  In close collaboration with the founders, and drawing on the input from users shared during
                  interviews, I iterated on potential color palettes and proportions of colors to explore and
                  define the palette of the brand.
                </p>
              </div>
            }
            right={
              <div className={d.colorMockPair}>
                <ExpandableImageButton
                  src={colorMock2}
                  alt={COLOR_MOCK_ALTS[1]}
                  imgClassName={d.colorMockPairImg}
                  buttonClassName={d.expandableImageButtonAuto}
                  onOpen={openLightbox}
                />
                <ExpandableImageButton
                  src={colorMock1}
                  alt={COLOR_MOCK_ALTS[0]}
                  imgClassName={d.colorMockPairImg}
                  buttonClassName={d.expandableImageButtonAuto}
                  onOpen={openLightbox}
                />
              </div>
            }
          />
          <div className={`${d.blockStack} ${d.mediaCaptionStack}`}>
            <div className={d.colorMockWide}>
              <ExpandableImageButton
                src={colorMock3}
                alt={COLOR_MOCK_ALTS[2]}
                imgClassName={d.mediaImg}
                onOpen={openLightbox}
              />
              <ExpandableImageButton
                src={colorMock4}
                alt={COLOR_MOCK_ALTS[3]}
                imgClassName={d.mediaImg}
                onOpen={openLightbox}
              />
            </div>
            <p className={d.mediaCaption}>Four hi-fidelity mockups of color palette iterations</p>
          </div>
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={<ColorSwatches />}
            right={
              <div className={d.cardCopy}>
                <p>To balance a color system that was evocative of golf yet communicated a more modern, inclusive product I drew on these colors:</p>
                <ul>
                  <li>
                    <strong>dark green</strong>: an ode to the course and nod to the tradition of golf
                  </li>
                  <li>
                    <strong>warm tan</strong>: referencing the sand on the course and a warm neutral alternative
                    to the industry standard bright white
                  </li>
                  <li>
                    <strong>neon green</strong>: a modern, bold color that nods to the course while being bold,
                    uncommon and playful
                  </li>
                  <li>
                    <strong>light pink</strong>: a complimentary color to green, an unexpected accent that would
                    be the brand signature and invite a broader audience
                  </li>
                </ul>
              </div>
            }
          />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <figure className={d.inlineQuote}>
                <img
                  className={d.inlineQuoteAvatar}
                  src={quoteAvatarP9}
                  alt=""
                  loading="lazy"
                />
                <blockquote className={d.inlineQuoteText}>
                  <p>&ldquo;I am surprised by the pink. I&apos;ve never seen that in golf before.&rdquo;</p>
                  <p>- Participant 9</p>
                </blockquote>
              </figure>
            }
            right={
              <div className={d.cardCopy}>
                <p>Color became one of the most debated parts of the project.</p>
                <p>
                  During testing, some older male users viewed pink accent tones as unconventional for golf
                  branding. Rather than removing the direction entirely, I refined how those colors were used
                  throughout the system to maintain differentiation while improving balance and scalability.
                </p>
                <p>
                  This iterative process reinforced how strongly visual conventions shape perception within
                  traditional industries.
                </p>
              </div>
            }
          />
          <p className={d.proseCallout}>
            Color became one of the most debated parts of the project...reinforcing how strongly visual
            conventions shape perception within traditional industries.
          </p>
        </div>
      </CaseStudyFindingBlock>

      <SectionDivider />

      <CaseStudyFindingBlock>
        <div className={d.findingStack}>
          <CaseStudyFindingHeader showMark={false} title="Typography: Choosing a Font That Represents Our Voice" />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.cardCopy}>
                <p>
                  Another key aspect of the visual strategy was deciding the typography. To define a font, or set
                  of fonts, I had to work with a few constraints:
                </p>
                <ul>
                  <li>
                    <strong>Business constraint</strong>: works for both digital interfaces as well as print &amp;
                    marketing (merch, wearables, etc.)
                  </li>
                  <li>
                    <strong>Technical constraint</strong>: font set that scales well for both native mobile app,
                    web platform, socials
                  </li>
                </ul>
                <p>In addition, I wanted to ensure the font set:</p>
                <ul>
                  <li>paired well with the typeface of the pre-existing logo</li>
                  <li>was accessible (met 508 WCAG guidance)</li>
                  <li>and aligned with the ethos and mission of the business</li>
                </ul>
                <p>
                  To explore type faces, I iterated on many fonts and font pairings. I designed hi-fidelity
                  mockups for web and mobile interfaces for feedback from stakeholders.
                </p>
              </div>
            }
            right={
              <div>
                <ExpandableImageButton
                  src={typographySelections}
                  alt={TYPOGRAPHY_SELECTIONS_ALT}
                  imgClassName={`${d.mediaImg} ${d.mediaImgNoShadow}`}
                  onOpen={openLightbox}
                />
                <p className={d.mediaCaption}>Selected Mockups of Typography Selections</p>
              </div>
            }
          />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow} ${d.doubleCardRowMarginBottom120}`}
            left={
              <img
                className={`${d.mediaImg} ${d.mediaImgNoShadow}`}
                src={typographyPoppinsSpec}
                alt="Poppins type specimen showing weights and character set"
                loading="lazy"
              />
            }
            right={
              <div className={d.cardCopy}>
                <p>
                  After many iterations, and with great excitement, we chose Poppins as the signature brand
                  typeface. This font met all our base criteria and above all, aligned with the brand ethos.
                </p>
                <p>
                  <strong>
                    Poppins was approachable, relaxed, and fun, just like the community we were building.
                  </strong>
                </p>
                <p>
                  The curved lines of the font face blended well with the logo type and the sans-serif font
                  exceeded accessibility guidelines. Poppins also had a diverse set of weights so it allowed for
                  seamless scaling and variation in designing both digital and print products.
                </p>
              </div>
            }
          />
          <figure className={d.figureCenter}>
            <img
              className={d.mediaImgNoShadow}
              src={typographyUiMockups}
              alt="High-fidelity mockups of typography in UI elements"
              loading="lazy"
            />
            <p className={d.mediaCaption}>High-fidelity mockups of typography in UI elements</p>
          </figure>
        </div>
      </CaseStudyFindingBlock>

      <SectionDivider />

      <CaseStudyFindingBlock>
        <div className={d.findingStack}>
          <CaseStudyFindingHeader showMark={false} title="Iconography & UI Elements: Crafting a Cohesive Identity" />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.cardCopy}>
                <p>
                  To build a UI system for a native mobile app and web platform, I needed to build upon the
                  foundation of color and type with UI/UX best practices to define icons and elements.
                </p>
                <p>To craft a cohesive identity, my priorities were:</p>
                <ul>
                  <li>scalable system for mobile &amp; web</li>
                  <li>ensuring A11y best practices</li>
                  <li>unifying visual strategy with brand ethos</li>
                </ul>
              </div>
            }
            right={
              <div className={d.uiMedia}>
                <img
                  className={d.mediaImgNoShadow}
                  src={uiLoginForm}
                  alt="Login form UI mockup with email and password fields"
                  loading="lazy"
                />
              </div>
            }
          />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.uiMedia}>
                <img
                  className={d.mediaImgNoShadow}
                  src={uiIconsGrid}
                  alt="Grid of rounded golf and navigation icons"
                  loading="lazy"
                />
              </div>
            }
            right={
              <div className={d.cardCopy}>
                <p>
                  For iconography, I sought out an icon set that was accessible and leveraged icons that were
                  familiar to users to ensure learnability and approachability.
                </p>
                <p>
                  To anchor the icons in the brand system, I chose a set that had soft edges to echo the curves
                  of the font and soften the styling away from the industry&apos;s more traditional sharp lines.
                </p>
              </div>
            }
          />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.cardCopy}>
                <p>
                  When crafting UI elements including mobile and web buttons, form elements, menus, cards, etc. I
                  applied the same priorities to my design strategy. I ensured font styling, structure and layout
                  prioritized accessibility standards.
                </p>
                <p>
                  I leveraged design elements with softened corners and ample whitespace to create a modern,
                  bold and approachable interface.
                </p>
              </div>
            }
            right={
              <div className={d.uiMedia}>
                <img
                  className={d.mediaImgNoShadow}
                  src={uiButtons}
                  alt="UI button variations including Log In, Follow, and circular action button"
                  loading="lazy"
                />
              </div>
            }
          />
        </div>
      </CaseStudyFindingBlock>

      <SectionDivider />

      <CaseStudyFindingBlock>
        <div className={d.findingStack}>
          <CaseStudyFindingHeader showMark={false} title="Imagery & Photography" />
          <CaseStudyDoubleCardRow
            className={`${cards.doubleRowAlignStart} ${d.findingDoubleRow}`}
            left={
              <div className={d.cardCopy}>
                <p>
                  No Bunkers No Bogeys Golf Club is on a mission to build community for under-represented players
                  in golf. When it came to imagery, this is where the brand could really &ldquo;put a face to
                  their name.&rdquo;
                </p>
                <p>
                  For imagery, I wanted to focus on the natural beauty of golf: real players in real landscapes.
                  The image direction was clear, ensure photography was of real players, highlighting diversity
                  across age, race, culture, presentation, identity and location.
                </p>
                <p>
                  To further speak to the brand&apos;s values, we focused on photos that emphasized connection,
                  people bonding over the game. After all, that is the backbone to the brand&apos;s offerings.
                </p>
                <p>
                  For consistency with the UI and visual identity, treatment of images was softened with rounded
                  corners to create a more bold, welcoming and modern look to golf.
                </p>
              </div>
            }
            right={
              <img
                className={`${d.mediaImg} ${d.mediaImgNoShadow}`}
                src={photographyCollage}
                alt="Collage of diverse golfers on course photography with rounded corners"
                loading="lazy"
              />
            }
          />
        </div>
      </CaseStudyFindingBlock>

      <ImageLightbox
        image={image}
        onClose={closeLightbox}
        closeButtonRef={closeButtonRef}
      />
    </div>
  );
}
